import React, { useEffect, useState } from 'react';
import UserDashboard from './UserDashboard';
import attendanceAPI from '../api/attendanceAPI';
import Swal from 'sweetalert2';
import { FaMapMarkerAlt, FaExternalLinkAlt } from 'react-icons/fa';

// ── Location status badge ─────────────────────────────────────────────────────
const LocationBadge = ({ status, distance }) => {
  const styles = {
    onsite:      'bg-green-100 text-green-700',
    remote:      'bg-blue-100 text-blue-700',
    verified:    'bg-green-100 text-green-700',
    outside:     'bg-red-100 text-red-700',
    not_checked: 'bg-gray-100 text-gray-500',
  };
  const labels = {
    onsite:      'On-site ✓',
    remote:      'Remote ✓',
    verified:    'Verified ✓',
    outside:     'Outside ✗',
    not_checked: '—',
  };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${styles[status] || styles.not_checked}`}>
      {labels[status] || '—'}
      {distance != null && status !== 'not_checked' && (
        <span className="ml-1 font-normal">({distance}m)</span>
      )}
    </span>
  );
};

// ── Calculate total hours from sessions (client-side fallback) ────────────────
// Used when rec.totalHours is missing or "00:00" but sessions exist
const calcTotalHours = (sessions, date) => {
  if (!sessions?.length) return null;
  let totalMs = 0;
  sessions.forEach((s) => {
    if (s.loginTime && s.logoutTime) {
      const login  = new Date(`${date}T${s.loginTime}`);
      const logout = new Date(`${date}T${s.logoutTime}`);
      if (!isNaN(login) && !isNaN(logout)) totalMs += logout - login;
    }
  });
  if (totalMs === 0) return null;
  const h = Math.floor(totalMs / (1000 * 60 * 60));
  const m = Math.floor((totalMs / (1000 * 60)) % 60);
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
};

// ─────────────────────────────────────────────────────────────────────────────
const OfficeAttendance = () => {
  const [records, setRecords]             = useState([]);
  const [searchQuery, setSearchQuery]     = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const fetchRecords = () => {
    attendanceAPI
      .get('/')
      .then((res) => setRecords(res.data))
      .catch((err) => console.error(err));
  };

  useEffect(() => { fetchRecords(); }, []);

  // ── Filters ───────────────────────────────────────────────────────────────
  const filteredRecords = records
    .filter((rec) => {
      const s = searchQuery.toLowerCase();
      return (
        rec.username?.toLowerCase().includes(s) ||
        rec.date?.includes(searchQuery) ||
        rec.workMode?.toLowerCase().includes(s) ||
        rec.status?.toLowerCase().includes(s) ||
        rec.role?.toLowerCase().includes(s)
      );
    })
    .filter((rec) => {
      if (!selectedMonth) return true;
      return rec.date?.slice(0, 7) === selectedMonth;
    });
const isAfterCutoffTime = () => {
  const now = new Date();

  // Convert to IST
  const istTime = new Date(
    now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" })
  );

  const hours = istTime.getHours();
  const minutes = istTime.getMinutes();

  return hours > 23 || (hours === 23 && minutes >= 50);
};
  // ── Force logout — uses /office-logout which accepts Office/HR token ──────
  const handleForceLogout = (rec) => {
    // Extract the real workerId — could be populated object or raw ID
    const wId = rec.workerId?._id || rec.workerId;

    Swal.fire({
      title:              'Logout Worker?',
      text:               `Force logout ${rec.username}?`,
      icon:               'warning',
      showCancelButton:   true,
      confirmButtonColor: '#d33',
      cancelButtonColor:  '#3085d6',
      confirmButtonText:  'Yes, Logout',
    }).then((result) => {
      if (result.isConfirmed) {
        attendanceAPI
          .post('/office-logout', { workerId: wId })   // ✅ uses /office-logout
          .then(() => {
            Swal.fire('✅ Success', `${rec.username} logged out.`, 'success');
            fetchRecords();
          })
          .catch((err) => {
            Swal.fire('Error', err.response?.data?.message || 'Logout failed', 'error');
            console.error(err);
          });
      }
    });
  };

  // ── Location details popup ────────────────────────────────────────────────
  const handleViewLocation = (rec) => {
    const sessions    = rec.sessions || [];
    const hasLocation = sessions.some((s) => s.currentLat != null) || rec.lat != null;

    if (!hasLocation) {
      Swal.fire({
        title: 'Location Info',
        html:  '<p style="color:#6b7280;">No location data recorded for this attendance.</p>',
        icon:  'info',
        confirmButtonColor: '#6b21a8',
      });
      return;
    }

    const sessionRows = sessions.map((s, i) => {
      const lat  = s.currentLat ?? rec.lat;
      const lng  = s.currentLng ?? rec.lng;
      const addr = s.currentAddress || (lat ? `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}` : '—');
      const mapLink = lat && lng
        ? `<a href="https://maps.google.com/?q=${lat},${lng}" target="_blank"
             style="color:#6b21a8;text-decoration:underline;">View on Map ↗</a>`
        : '—';
      const statusLabel = {
        onsite: '✅ On-site', remote: '✅ Remote',
        outside: '❌ Outside', not_checked: '—',
      }[s.locationStatus] || '—';

      return `
        <div style="border:1px solid #e9d5ff;border-radius:8px;padding:10px;margin-bottom:8px;text-align:left;background:#faf5ff;">
          <div style="font-weight:600;color:#6b21a8;margin-bottom:4px;">
            Session ${i + 1} — ${s.loginTime || '—'} → ${s.logoutTime || '<span style="color:#f97316;">active</span>'}
          </div>
          <div style="font-size:0.85rem;color:#374151;">
            <div>📍 <strong>Address:</strong> ${addr}</div>
            <div>📏 <strong>Distance from base:</strong> ${s.distanceFromBase != null ? s.distanceFromBase + 'm' : '—'}</div>
            <div>🔖 <strong>Status:</strong> ${statusLabel}</div>
            <div style="margin-top:4px;">${mapLink}</div>
          </div>
        </div>`;
    }).join('');

    Swal.fire({
      title:              `${rec.username} — Location Details`,
      html:               sessionRows || '<p>No sessions found.</p>',
      confirmButtonColor: '#6b21a8',
      width:              600,
    });
  };

  // ── CSV download ──────────────────────────────────────────────────────────
  const downloadCSV = () => {
    const headers = [
      'Date','Name','Company','Role','Team','Work Mode',
      'Login Time','Logout Time','Location Address','Distance (m)','Location Status',
      'Total Hours','Overtime','Status',
    ];

    const rows = filteredRecords.flatMap((rec) => {
      const isAbsent  = rec.status === 'Absent';
      const totalHrs  = rec.totalHours || calcTotalHours(rec.sessions, rec.date) || 'N/A';
      if (rec.sessions?.length > 0 && !isAbsent) {
        return rec.sessions.map((s) => [
          rec.date ? `'${rec.date}` : 'N/A',
          rec.username, rec.company || 'N/A', rec.role, rec.team || 'N/A', rec.workMode || 'N/A',
          s.loginTime  || 'N/A',
          s.logoutTime || 'N/A',
          s.currentAddress || (rec.lat ? `${Number(rec.lat).toFixed(5)},${Number(rec.lng).toFixed(5)}` : 'N/A'),
          s.distanceFromBase != null ? s.distanceFromBase : 'N/A',
          s.locationStatus  || 'N/A',
          totalHrs,
          rec.overtime || 'N/A',
          rec.status,
        ]);
      } else {
        return [[
          rec.date ? `'${rec.date}` : 'N/A',
          rec.username, rec.company || 'N/A', rec.role, rec.team || 'N/A', rec.workMode || 'N/A',
          'N/A','N/A','N/A','N/A','N/A',
          isAbsent ? 'N/A' : totalHrs,
          isAbsent ? 'N/A' : rec.overtime || 'N/A',
          rec.status,
        ]];
      }
    });

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((v) => `"${v}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href  = URL.createObjectURL(blob);
    link.setAttribute('download', 'attendance_report.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <UserDashboard />
      <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-100 p-8 pt-20">
        <h2 className="text-3xl font-bold font-mono text-purple-700 mb-2 mt-4 text-center">
          Attendance Report
        </h2>

        <div className="bg-white shadow-md rounded-xl p-4 mb-6">

          {/* Filters */}
          <div className="max-w-8xl mx-auto mb-4 flex flex-col lg:flex-row justify-between items-center gap-4">
            <input
              type="text"
              placeholder="Search by name, date, role, work mode, status…"
              className="px-4 py-2 border rounded-lg w-full lg:w-1/3"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full lg:w-auto">
              <label className="text-purple-700 font-semibold whitespace-nowrap">Select Month:</label>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="border rounded-lg p-2 w-full sm:w-auto"
              />
            </div>
            <button
              onClick={() => {
                Swal.fire({
                  title: 'Download Report?',
                  text:  'Download attendance report as CSV?',
                  icon:  'question',
                  showCancelButton:   true,
                  confirmButtonColor: '#6b21a8',
                  cancelButtonColor:  '#d33',
                  confirmButtonText:  'Yes',
                  cancelButtonText:   'No',
                }).then((result) => { if (result.isConfirmed) downloadCSV(); });
              }}
              className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 w-full sm:w-auto"
            >
              Download CSV
            </button>
          </div>

          {/* Table */}
          <div className="md:max-h-[450px] max-h-[500px] overflow-auto">
            <table className="min-w-full table-auto text-sm">
              <thead className="bg-purple-100 text-purple-700">
                <tr>
                  {['Date','Profile','Name','Company','Role','Team','Work Mode','Login','Logout','Location','Total Hrs','Overtime','Status'].map((h) => (
                    <th key={h} className="py-3 px-3 sticky top-0 bg-purple-100 text-left whitespace-nowrap z-10">{h}</th>
                  ))}
                </tr>
              </thead>

              <tbody className="text-gray-700">
                {filteredRecords.map((rec, idx) => {
                  // ── Fix total hours: use stored value OR calculate from sessions ──
                  const displayTotalHours =
                    (rec.totalHours && rec.totalHours !== '00:00')
                      ? rec.totalHours
                      : calcTotalHours(rec.sessions, rec.date) || '—';

                  const displayOvertime =
                    rec.overtime && rec.overtime !== '00:00' ? rec.overtime : '—';

                  return (
                    <tr key={idx} className="border-t hover:bg-purple-50 transition">

                      {/* Date */}
                      <td className="py-3 px-3 whitespace-nowrap text-xs">
                        {new Date(rec.date).toLocaleDateString('en-GB')}
                      </td>

                      {/* Profile */}
                      <td className="py-3 px-3">
                        <img
                          src={rec.profileImage || rec.workerId?.profileImage}
                          alt="profile"
                          className="w-9 h-9 rounded-full object-cover border border-purple-200"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </td>

                      {/* Name */}
                      <td className="py-3 px-3 font-semibold text-purple-700">{rec.username}</td>

                      {/* Company */}
                      <td className="py-3 px-3 text-xs">{rec.company || '—'}</td>

                      {/* Role */}
                      <td className="py-3 px-3 text-xs">{rec.role}</td>

                      {/* Team */}
                      <td className="py-3 px-3 text-xs">{rec.team || '—'}</td>

                      {/* Work Mode */}
                      <td className="py-3 px-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          rec.workMode === 'Remote' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {rec.workMode || '—'}
                        </span>
                      </td>

                      {/* Login times */}
                      <td className="py-3 px-3">
                        {rec.sessions?.length > 0
                          ? rec.sessions.map((s, i) => (
                              <div key={i} className="text-xs">{s.loginTime || '—'}</div>
                            ))
                          : <span className="text-xs text-gray-400">—</span>}
                      </td>

                      {/* Logout times — with force-logout button if active */}
                      <td className="py-3 px-3">
                       {rec.sessions?.length > 0
  ? rec.sessions.map((s, i) => (
      <div key={i} className="text-xs">
        {s.logoutTime ? (
          s.logoutTime
        ) : !isAfterCutoffTime() ? (
          <button
            onClick={() => handleForceLogout(rec)}
            className="bg-red-500 text-white px-2 py-0.5 rounded hover:bg-red-600 text-xs"
          >
            Logout
          </button>
        ) : (
          <span className="text-gray-400 italic">Closed</span>
        )}
      </div>
    ))
                          : <span className="text-xs text-gray-400">—</span>}
                      </td>

                      {/* Location */}
                      <td className="py-3 px-3">
                        {rec.sessions?.length > 0 ? (
                          <div className="space-y-1">
                            {rec.sessions.map((s, i) => {
                              const lat = s.currentLat ?? rec.lat;
                              const lng = s.currentLng ?? rec.lng;
                              return (
                                <div key={i} className="flex items-center gap-1 flex-wrap">
                                  <LocationBadge
                                    status={s.locationStatus || 'not_checked'}
                                    distance={s.distanceFromBase}
                                  />
                                  {lat && lng && (
                                    <a
                                      href={`https://maps.google.com/?q=${lat},${lng}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-purple-500 hover:text-purple-700 flex items-center gap-0.5"
                                    >
                                      <FaMapMarkerAlt className="text-xs" />
                                      <FaExternalLinkAlt className="text-[10px]" />
                                    </a>
                                  )}
                                  <button
                                    onClick={() => handleViewLocation(rec)}
                                    className="text-xs text-purple-600 hover:text-purple-800 underline"
                                  >
                                    Detail
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>

                      {/* Total hours — stored OR calculated from sessions */}
                      <td className="py-3 px-3 font-medium text-xs">{displayTotalHours}</td>

                      {/* Overtime */}
                      <td className="py-3 px-3 text-orange-600 font-medium text-xs">{displayOvertime}</td>

                      {/* Status */}
                      <td className="py-3 px-3">
                        <span className={`font-semibold text-xs ${
                          rec.status === 'Present' ? 'text-green-600' : 'text-red-500'
                        }`}>
                          {rec.status}
                        </span>
                      </td>

                    </tr>
                  );
                })}

                {filteredRecords.length === 0 && (
                  <tr>
                    <td colSpan="13" className="text-center py-6 text-gray-500">
                      No attendance records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfficeAttendance;